import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../lib/auth-context";
import { api, fmtCurrency } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import Logo from "../../components/Logo";
import CreditGauge from "../../components/CreditGauge";
import NotificationBell from "../../components/NotificationBell";
import WelcomeTour from "../../components/WelcomeTour";
import { Plus, ArrowUp, History, Sparkles, ChevronRight } from "lucide-react";

const PRODUCTS = [
    { name: "NanoCredit", title: "NanoCredit™", subtitle: "Up to ₦50,000", color: "#00D084", bg: "bg-emerald-50", text: "text-emerald-700", btn: "bg-[#00D084] hover:bg-[#00b86d]", action: "Apply Now" },
    { name: "FlexCredit", title: "FlexCredit™", subtitle: "Available Limit ₦120,000", color: "#0066FF", bg: "bg-blue-50", text: "text-blue-700", btn: "bg-[#0066FF] hover:bg-[#0052cc]", action: "Get Funds" },
    { name: "SalaryAdvance", title: "SalaryAdvance™", subtitle: "Up to 50% of salary", color: "#FF6B00", bg: "bg-orange-50", text: "text-orange-700", btn: "bg-[#FF6B00] hover:bg-[#e55f00]", action: "Apply Now" },
    { name: "SplitPay", title: "SplitPay™", subtitle: "Borrow together, build together", color: "#7000FF", bg: "bg-purple-50", text: "text-purple-700", btn: "bg-[#7000FF] hover:bg-[#5d00d6]", action: "Apply Now" },
];

export default function ConsumerHome() {
    const { user } = useAuth();
    const { t } = useI18n();
    const [wallet, setWallet] = useState(null);
    const [score, setScore] = useState(null);
    const [scoreLoading, setScoreLoading] = useState(true);

    useEffect(() => {
        api.get("/wallet").then((r) => setWallet(r.data));
        api.get("/credit/score").then((r) => setScore(r.data)).finally(() => setScoreLoading(false));
    }, []);

    const firstName = user?.full_name?.split(" ")[0] || "Friend";

    return (
        <div data-testid="consumer-home">
            <WelcomeTour />
            {/* Header */}
            <div className="px-5 pt-8 pb-4 bg-white">
                <div className="flex items-center justify-between">
                    <Logo size={30} />
                    <NotificationBell testid="home-bell" />
                </div>

                <div className="mt-5 flex items-center justify-between">
                    <div>
                        <h1 className="font-display text-2xl font-semibold text-[#0A1930]">
                            {t("common.hello")}, {firstName} <span className="inline-block animate-pulse">👋</span>
                        </h1>
                        <p className="text-sm text-slate-500 mt-0.5">{t("home.good_to_see_you")}</p>
                    </div>
                    {user?.avatar_url && (
                        <img src={user.avatar_url} alt="" className="size-12 rounded-full object-cover ring-2 ring-emerald-300" data-testid="home-avatar" />
                    )}
                </div>
            </div>

            <div className="px-5 space-y-4">
                {/* Credit DNA Score */}
                <div className="bg-white rounded-2xl p-5 border border-slate-200/60 product-shadow" data-testid="dna-card">
                    <div className="flex justify-between items-start gap-4">
                        <div>
                            <div className="flex items-center gap-1.5">
                                <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("home.credit_dna")}</span>
                            </div>
                            <CreditGauge
                                score={score?.score || user?.credit_score || 720}
                                grade={score?.grade || "Very Good"}
                                size={150}
                                label=""
                            />
                        </div>
                        <div className="flex-1 max-w-[180px] pt-2">
                            <div className="font-display text-base font-semibold text-[#0A1930]">{t("home.your_on_track")}</div>
                            <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">
                                {scoreLoading ? t("common.loading") : score?.explanation || "Keep using credit responsibly to unlock better offers."}
                            </p>
                            <Link to="/consumer/credit/score" data-testid="view-score-btn">
                                <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#0066FF] bg-blue-50 border border-blue-200 rounded-lg px-3 py-1.5">
                                    <Sparkles size={12} /> {t("home.view_factors")}
                                </div>
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Wallet card */}
                <div className="rounded-3xl gradient-consumer gradient-noise p-5 text-white relative overflow-hidden" data-testid="wallet-card">
                    <div className="flex justify-between items-start">
                        <div>
                            <div className="text-xs uppercase tracking-[0.18em] text-white/70">{t("home.onepass_wallet")}</div>
                            <div className="font-display text-3xl font-bold mt-2" data-testid="wallet-balance">
                                {wallet ? fmtCurrency(wallet.balance, wallet.currency, { minDecimals: 2, maxDecimals: 2 }) : "—"}
                            </div>
                        </div>
                        <span className="text-[10px] px-2 py-1 rounded-md bg-white/15 backdrop-blur font-bold">
                            {wallet?.currency || "NGN"}
                        </span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 mt-5">
                        <Link to="/consumer/wallet/add" data-testid="quick-add-money">
                            <div className="bg-white/15 backdrop-blur rounded-xl p-3 text-center tap-feedback">
                                <Plus size={20} className="mx-auto" />
                                <div className="text-[11px] font-semibold mt-1">{t("home.add_money")}</div>
                            </div>
                        </Link>
                        <Link to="/consumer/wallet/send" data-testid="quick-send">
                            <div className="bg-white/15 backdrop-blur rounded-xl p-3 text-center tap-feedback">
                                <ArrowUp size={20} className="mx-auto" />
                                <div className="text-[11px] font-semibold mt-1">{t("home.send")}</div>
                            </div>
                        </Link>
                        <Link to="/consumer/activity" data-testid="quick-history">
                            <div className="bg-white/15 backdrop-blur rounded-xl p-3 text-center tap-feedback">
                                <History size={20} className="mx-auto" />
                                <div className="text-[11px] font-semibold mt-1">{t("home.history")}</div>
                            </div>
                        </Link>
                    </div>
                </div>

                {/* Available Credit */}
                <div>
                    <div className="flex items-center justify-between mb-3">
                        <h2 className="font-display font-semibold text-[#0A1930] text-base">{t("home.available_credit")}</h2>
                        <Link to="/consumer/credit" className="text-xs font-semibold text-[#0066FF]" data-testid="see-all-products">
                            {t("common.see_all")}
                        </Link>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        {PRODUCTS.map((p) => (
                            <Link
                                to={`/consumer/credit/${p.name}`}
                                key={p.name}
                                data-testid={`product-tile-${p.name}`}
                                className={`${p.bg} rounded-2xl p-4 border border-${p.color}/20 tap-feedback`}
                            >
                                <div className="flex items-center justify-between">
                                    <div className={`size-9 rounded-lg flex items-center justify-center ${p.text}`}
                                         style={{ background: `${p.color}25` }}>
                                        <Sparkles size={16} />
                                    </div>
                                </div>
                                <div className={`font-display font-semibold ${p.text} mt-3 text-sm`}>{p.title}</div>
                                <div className="text-[11px] text-slate-500 mt-0.5 leading-tight">{p.subtitle}</div>
                                <div className={`mt-3 text-center text-xs font-bold text-white rounded-lg py-1.5 ${p.btn}`}>
                                    {p.action}
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>

                {/* PrimeCredit banner */}
                <Link to="/consumer/credit/PrimeCredit" data-testid="prime-banner" className="block">
                    <div className="bg-gradient-to-r from-[#FF0055] to-[#FF6B00] rounded-2xl p-5 text-white flex items-center justify-between gradient-noise">
                        <div>
                            <div className="text-xs uppercase tracking-wider text-white/80">PrimeCredit™</div>
                            <div className="font-display text-lg font-semibold mt-1">Unlock larger loans</div>
                            <div className="text-xs text-white/80 mt-0.5">Better rates · up to ₦5M</div>
                        </div>
                        <ChevronRight size={28} />
                    </div>
                </Link>

                {/* Savings + Insurance entry tiles */}
                <div className="grid grid-cols-2 gap-3">
                    <Link to="/consumer/savings" data-testid="savings-tile" className="block">
                        <div className="bg-gradient-to-br from-[#00D084] to-[#00b073] rounded-2xl p-4 text-white gradient-noise h-full">
                            <div className="text-xs uppercase tracking-wider text-white/80">OnePass</div>
                            <div className="font-display text-lg font-semibold mt-0.5">{t("tile.savings.label")}</div>
                            <div className="text-[11px] text-white/80 mt-2 leading-tight">{t("tile.savings.desc")}</div>
                        </div>
                    </Link>
                    <Link to="/consumer/insurance" data-testid="insurance-tile" className="block">
                        <div className="bg-gradient-to-br from-[#0066FF] to-[#0044CC] rounded-2xl p-4 text-white gradient-noise h-full">
                            <div className="text-xs uppercase tracking-wider text-white/80">OnePass</div>
                            <div className="font-display text-lg font-semibold mt-0.5">{t("tile.insurance.label")}</div>
                            <div className="text-[11px] text-white/80 mt-2 leading-tight">{t("tile.insurance.desc")}</div>
                        </div>
                    </Link>
                    <Link to="/consumer/investments" data-testid="investments-tile" className="block">
                        <div className="bg-gradient-to-br from-[#F59E0B] to-[#d97706] rounded-2xl p-4 text-white gradient-noise h-full">
                            <div className="text-xs uppercase tracking-wider text-white/80">OnePass</div>
                            <div className="font-display text-lg font-semibold mt-0.5">{t("tile.investments.label")}</div>
                            <div className="text-[11px] text-white/80 mt-2 leading-tight">{t("tile.investments.desc")}</div>
                        </div>
                    </Link>
                    <Link to="/consumer/shop" data-testid="shop-tile" className="block">
                        <div className="bg-gradient-to-br from-[#7000FF] to-[#5d00d6] rounded-2xl p-4 text-white gradient-noise h-full">
                            <div className="text-xs uppercase tracking-wider text-white/80">OnePass</div>
                            <div className="font-display text-lg font-semibold mt-0.5">{t("tile.shop.label")}</div>
                            <div className="text-[11px] text-white/80 mt-2 leading-tight">{t("tile.shop.desc")}</div>
                        </div>
                    </Link>
                </div>
            </div>
        </div>
    );
}
